import { useBehaviourState } from '@sravni/react-silent-authentication-form';
import { usePrevious } from '@sravni/react-utils';
import React, { useCallback, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { SCREENS, FIELD_NAME } from '../constants';
import type { AuthInlineFormProps } from '../types';

import { isSatisfyOption } from '../../../helpers';
import { AuthInlineFormFields } from './AuthInlineFormFields';
import { AuthInlineFormDesktopScreen } from './AuthInlineFormDesktopScreen';
import { AuthInlineFormMobileScreen } from './AuthInlineFormMobileScreen';

export const AuthInlineFormView = (props: AuthInlineFormProps) => {
    const {
        isEnabledNameField = false,
        isEnabledEmailField = true,
        advertisingBanner,
        getOTPButtonProps,
        consentsScreenTitle,
        advertisingCheckboxLabel,
        onScreenChange,
        onChangeAgreementAdvertising,
        onChangeAgreementPersonalData,
        children,
        onChangePhoneClick,
        onClose,
        onConsentsScreenOpen,
        onOTPScreenOpen,
        onConsentsSubmitClick,
    } = props;

    const { setValue, watch, formState } = useFormContext();
    const { isConsentsReceived } = useBehaviourState();
    const [currentScreen, setCurrentScreen] = useState<ValuesOf<typeof SCREENS>>(SCREENS.INITIAL);
    const prevScreen = usePrevious(currentScreen);
    const isDialogVisible = isSatisfyOption([SCREENS.OTP, SCREENS.CONSENTS], currentScreen);
    const [submitAttempt, setSubmitAttempt] = useState(0);
    const [agreementPersonalData, agreementAdvertising] = watch([
        FIELD_NAME.AGREEMENT_PERSONAL_DATA,
        FIELD_NAME.AGREEMENT_ADVERTISING,
    ]);

    const onDialogClose = useCallback(() => {
        onClose?.();
        setValue(FIELD_NAME.OTP, undefined);
        setCurrentScreen(SCREENS.INITIAL);
    }, [setValue, onClose]);

    const handleChangePhoneClick = useCallback(() => {
        onChangePhoneClick?.();
        setValue(FIELD_NAME.OTP, undefined);
        setCurrentScreen(SCREENS.INITIAL);
    }, [setValue, onChangePhoneClick]);

    const onReturnButtonClick = useCallback(() => {
        setValue(FIELD_NAME.OTP, undefined);
        setCurrentScreen(SCREENS.OTP);
    }, [setValue]);

    useEffect(() => {
        if (isConsentsReceived === false) {
            setCurrentScreen(SCREENS.CONSENTS);
        }
    }, [isConsentsReceived]);

    useEffect(() => {
        if (typeof prevScreen !== 'undefined' && prevScreen !== currentScreen) onScreenChange?.(currentScreen);
    }, [currentScreen, onScreenChange, prevScreen]);

    useEffect(() => {
        setSubmitAttempt(0);
    }, [agreementAdvertising, agreementPersonalData]);

    const onConsentsSubmitButtonClick: React.MouseEventHandler<HTMLDivElement> = useCallback(
        (event) => {
            if (!agreementAdvertising && submitAttempt === 0) {
                event.preventDefault();
                event.stopPropagation();
                setSubmitAttempt((prev) => prev + 1);
                onConsentsSubmitClick?.({
                    agreementPersonalData,
                    agreementAdvertising,
                    isSubmitted: false,
                });
            } else {
                onConsentsSubmitClick?.({
                    agreementPersonalData,
                    agreementAdvertising,
                    isSubmitted: true,
                });
            }
        },
        [agreementAdvertising, submitAttempt, setSubmitAttempt, onConsentsSubmitClick, agreementPersonalData],
    );

    return (
        <>
            <AuthInlineFormFields
                isEnabledNameField={isEnabledNameField}
                isEnabledEmailField={isEnabledEmailField}
                getOTPButtonProps={getOTPButtonProps}
                currentScreen={currentScreen}
                setCurrentScreen={setCurrentScreen}>
                {children}
            </AuthInlineFormFields>

            <AuthInlineFormMobileScreen
                currentScreen={currentScreen}
                submitAttempt={submitAttempt}
                setSubmitAttempt={setSubmitAttempt}
                onConsentsSubmitClick={onConsentsSubmitButtonClick}
                onClose={onDialogClose}
                onChangePhoneClick={handleChangePhoneClick}
                consentsScreenTitle={consentsScreenTitle}
                onOTPScreenOpen={onOTPScreenOpen}
                advertisingCheckboxLabel={advertisingCheckboxLabel}
                onChangeAgreementAdvertising={onChangeAgreementAdvertising}
                onChangeAgreementPersonalData={onChangeAgreementPersonalData}
                onConsentsScreenOpen={onConsentsScreenOpen}
                isDialogVisible={isDialogVisible}
                onReturnButtonClick={onReturnButtonClick}
            />

            <AuthInlineFormDesktopScreen
                currentScreen={currentScreen}
                setCurrentScreen={setCurrentScreen}
                submitAttempt={submitAttempt}
                onConsentsSubmitClick={onConsentsSubmitButtonClick}
                onClose={onDialogClose}
                onChangePhoneClick={handleChangePhoneClick}
                consentsScreenTitle={consentsScreenTitle}
                advertisingBanner={advertisingBanner}
                onOTPScreenOpen={onOTPScreenOpen}
                advertisingCheckboxLabel={advertisingCheckboxLabel}
                onChangeAgreementAdvertising={onChangeAgreementAdvertising}
                onChangeAgreementPersonalData={onChangeAgreementPersonalData}
                onConsentsScreenOpen={onConsentsScreenOpen}
                isDialogVisible={isDialogVisible}
                onReturnButtonClick={onReturnButtonClick}
            />
        </>
    );
};
